'use strict';

export default angular
  .module('maestro.services.data.challenges', [
    'maestro.services.webcore'
  ])
  .service('ChallengesService', ChallengesService)
  .name;

function ChallengesService(webcore, $q) {

  /**
   * Create a challenge from scratch or from a template
   * @param {number} organizationId Organization ID to create this challenge for.
   * @param {*} challengeModel
   * @param [params] Request parameters
   * @param {boolean} [params.check] true - Check if an active challenge already exists without creating one.
   *     false - Do not check if an active challenge already exists, just create it, default.
   * @param {number} [params.parentId] Challenge or template ID to copy settings from.
   * @returns {Promise}
   */
  this.createChallenge = (organizationId, challengeModel, params) => {
    return $q.when(
      webcore.services.challenge.createChallenge(organizationId, challengeModel, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * View a list of challenges selected by their parameters. Only admins can see the templates in the search results.
   * @param {number} organizationId Organization ID.
   * @param [params] Request params
   * @param {number} [params.status] Optional challenges status filter
   * @param {number} [params.challengeId] Retrieve only this challenge
   * @param {*} [params.challengeType] Filter response by challenge types. Multiple values are supported.
   * @param {string} [params.searchBy] Search by name. Use * for a wildcard.
   * @param {number} [params.parentId] Template ID to filter by
   * @returns {Promise}
   */
  this.getChallenges = (organizationId, params) => {
    let parameters = params || {};
    parameters.sortCollection = 'challenges';
    parameters.sortBy = 'challengeId';
    parameters.sortOrder = 'desc';
    return $q.when(
      webcore.services.challenge.getChallenges(organizationId, parameters)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get challenge by ID
   * @param {number} organizationId Organization ID.
   * @param {number} challengeId Challenge ID
   * @returns {Promise}
   */
  this.getChallenge = (organizationId, challengeId) => {
    return $q.when(
      webcore.services.challenge.getChallenges(organizationId, { challengeId: challengeId })
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates specified challenge in the specified organization.
   * An administrator can modify all template fields except template and challengeType.
   * @param {number} organizationId Organization ID to update a challenge within
   * @param {number} challengeId Challenge ID to update
   * @param {*} updateChallengeModel
   * @returns {Promise}
   */
  this.updateChallenge = (organizationId, challengeId, updateChallengeModel) => {
    return $q.when(
      webcore.services.challenge.updateChallenge(organizationId, challengeId, updateChallengeModel)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Deletes specified challenge from the specified organization.
   * @param {number} organizationId Organization ID to update a challenge from
   * @param {number} challengeId Challenge ID to delete
   * @returns {Promise}
   */
  this.deleteChallenge = (organizationId, challengeId) => {
    return $q.when(
      webcore.services.challenge.deleteChallenge(organizationId, challengeId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates specified challenge's status with new value.
   * @param {number} organizationId Organization ID to update
   * @param {number} challengeId Challenge ID to update
   * @param {number} status New status:
   *     0 - Inactive challenge
   *     1 - Active challenge
   * @returns {Promise}
   */
  this.updateChallengeStatus = (organizationId, challengeId, status) => {
    return $q.when(
      webcore.services.challenge.updateChallengeStatus(organizationId, challengeId, status)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Creates or updates activity or a list of activities for the specified challenge.
   * @param {number} organizationId Organization ID.
   * @param {number} challengeId Id of the challenge to create/update activities for.
   * @param {*} activityModel Activity data to submit.
   * @returns {Promise}
   */
  this.createOrUpdateActivity = (organizationId, challengeId, activityModel) => {
    return $q.when(
      webcore.services.challenge.createOrUpdateActivity(organizationId, challengeId, activityModel)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets user activities for the specified challenge and status.
   * @param {number} organizationId Organization ID.
   * @param {number} challengeId Challenge ID to retrieve activities for
   * @param [params] Request parameters.
   * @param {number} [params.status] Status filter
   *     0 - Inactive activities
   *     1 - Active activities
   * @param {number} [params.userId] User ID for use by administrators
   * @returns {Promise}
   */
  this.getUserActivities = (organizationId, challengeId, params) => {
    return $q.when(
      webcore.services.challenge.getUserActivities(organizationId, challengeId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets challenge winners according to the specified filter conditions.
   *
   * Only members of the given organization can retrieve the winners.
   *
   * Winners may contain the place they ranked, user or group information, their result (for example, the number of users recruited or the % energy saved),
   * current month's usage, and usage from this month last year.
   * @param {number} organizationId Organization to get challenge winners for.
   * @param {number} challengeId Challenge ID to obtain winners for
   * @param [params] Request parameters.
   * @param {string} [params.startDate] Optional start date of the challenge
   * @param {string} [params.endDate] Optional end date of the challenge
   * @param {number} [params.winnersLimit] Optional limit to the maximum number of winners per period returned
   * @returns {Promise}
   */
  this.getChallengeWinners = (organizationId, challengeId, params) => {
    return $q.when(
      webcore.services.challenge.getChallengeWinners(organizationId, challengeId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets challenge's participants according to the filter criterias.
   * @param {number} organizationId Organization to get challenge participants for.
   * @param {number} challengeId Challenge to get participants for.
   * @param [params] Request parameters.
   * @param {number} [params.status] Participation status filter.
   * @param {number} [params.userId] Filter the response by user ID.
   * @returns {Promise}
   */
  this.getChallengeParticipants = (organizationId, challengeId, params) => {
    return $q.when(
      webcore.services.challenge.getChallengeParticipants(organizationId, challengeId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates challenge participant's status.
   *
   * Only approved members of the given organization and the challenge participants can update their participation status to Opt-In or
   * Opt-Out.
   * @param {number} organizationId Organization ID.
   * @param {number} challengeId Challenge ID to update participation status in.
   * @param params Request parameters
   * @param {number} params.status New participation status to set.
   * @param {number} [params.userId] When called by the administrator only.
   * @returns {Promise}
   */
  this.updateChallengeParticipationStatus = (organizationId, challengeId, params) => {
    return $q.when(
      webcore.services.challenge.updateChallengeParticipationStatus(organizationId, challengeId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Return demand response challenge participants energy usage and baseline for the challenge period. The first record contains total information for the
   * whole challenge period. Rest records contain 5 minute interval data.
   *
   * Only approved members of the given organization and the challenge participants can call this API.
   * @param {number} organizationId Organization ID.
   * @param {number} challengeId Challenge ID to get energy usage for.
   * @param [params] Request parameters.
   * @param {number} [params.userId] To get specific user details.
   * @returns {Promise}
   */
  this.getDemandResponseChallengeEnergyUsage = (organizationId, challengeId, params) => {
    return $q.when(
      webcore.services.challenge.getDemandResponseChallengeEnergyUsage(organizationId, challengeId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

}
